<template>
  <div class="modal fade show modal-style" aria-labelledby="incentiveAdjustModal">
    <div
        class="modal-dialog modal-lg modal-dialog-centered"
        :style="{ transform: `translate(${pos.x}px, ${pos.y}px)`, cursor: dragging ? 'grabbing' : 'default' }"
    >
      <div class="modal-content">
        <div
            class="d-flex justify-content-between px-2 my-1 align-items-center"
            style="cursor: grab; user-select: none;"
            @mousedown="startDrag"
        >
          <h5 class="mb-0">Incentive Adjustment — {{ saleMaster.bill_number }}</h5>
          <button type="button" class="btn-close" @mousedown.stop @click="$emit('close')"></button>
        </div>
        <hr class="mt-0 mb-0">
        <div class="px-2 pt-2 d-flex align-items-center gap-2">
          <label class="mb-0 text-nowrap fw-semibold">Incentive %</label>
          <input
              type="number"
              class="form-control form-control-sm"
              style="max-width:120px"
              v-model.number="globalPercentage"
              @input="applyGlobal"
              min="0"
              max="100"
              placeholder="Apply to all"
          />
        </div>
        <div class="px-2 py-1">
          <div class="table-responsive">
            <table class="table table-bordered table-sm">
              <thead>
              <tr>
                <th>Product</th>
                <th class="text-right">Rate</th>
                <th class="text-right">Qty</th>
                <th class="text-right">Total Amount</th>
                <th class="text-right">Incentive On</th>
                <th class="text-right">Default Incentive %</th>
                <th class="text-right" style="min-width:120px">Incentive %</th>
                <th class="text-right">Incentive Amount</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, i) in items" :key="i">
                <td>{{ item.product_name }}</td>
                <td class="text-right">{{ item.rate }}</td>
                <td class="text-right">{{ item.quantity }}</td>
                <td class="text-right">{{ item.total_amount }}</td>
                <td class="text-right">{{ item.incentive_base_amount }}</td>
                <td class="text-right">{{ item.default_incentive_percentage }}</td>
                <td>
                  <input
                      type="number"
                      class="form-control form-control-sm text-right"
                      v-model.number="item.incentive_percentage"
                      @input="recalculate(item)"
                      min="0"
                      max="100"
                  />
                </td>
                <td class="text-right">{{ item.incentive_amount }}</td>
              </tr>
              <tr>
                <td colspan="3"><strong>Total</strong></td>
                <td class="text-right"><strong>{{ totals.total_amount }}</strong></td>
                <td class="text-right"><strong>{{ totals.incentive_base_amount }}</strong></td>
                <td></td>
                <td></td>
                <td class="text-right"><strong>{{ totals.incentive_amount }}</strong></td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="d-flex gap-2 justify-content-between align-items-center px-2 pb-2">
          <input type="date" class="form-control form-control-sm" style="max-width:160px" v-model="localDate" />
          <div class="d-flex gap-2">
            <button class="btn btn-outline-secondary btn-sm" @click="$emit('close')">Cancel</button>
            <button class="btn btn-primary btn-sm" :disabled="saving" @click="onSave(false)">Save</button>
            <button class="btn btn-success btn-sm" :disabled="saving" @click="onSave(true)">Save & Approve</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, inject, computed, onUnmounted } from 'vue';
import useDate from '@/services/utils/day';
import handleHospital from '@/services/modules/hospital';
import { useRoute } from 'vue-router';

const props = defineProps({
  saleMaster: { type: Object, required: true },
  items:      { type: Array,  required: true },
  referrerId: { type: [Number, String], required: true },
});

const emit = defineEmits(['close', 'saved']);

const route       = useRoute();
const showError   = inject('showError');
const showSuccess = inject('showSuccess');
const { currentDate } = useDate();
const saving          = ref(false);
const localDate       = ref(currentDate());
const globalPercentage = ref(null);

const { storeIncentiveAdjustment } = handleHospital();

const pos     = ref({ x: 0, y: 0 });
const dragging = ref(false);
const origin  = ref({ x: 0, y: 0 });

const startDrag = (e) => {
  dragging.value = true;
  origin.value = { x: e.clientX - pos.value.x, y: e.clientY - pos.value.y };
  window.addEventListener('mousemove', onDrag);
  window.addEventListener('mouseup', stopDrag);
};

const onDrag = (e) => {
  if (!dragging.value) return;
  pos.value = { x: e.clientX - origin.value.x, y: e.clientY - origin.value.y };
};

const stopDrag = () => {
  dragging.value = false;
  window.removeEventListener('mousemove', onDrag);
  window.removeEventListener('mouseup', stopDrag);
};

onUnmounted(() => {
  window.removeEventListener('mousemove', onDrag);
  window.removeEventListener('mouseup', stopDrag);
});

const roundAmount = (value) => parseFloat(value.toFixed(4));

const totals = computed(() => props.items.reduce((acc, item) => {
  acc.total_amount          = roundAmount(acc.total_amount + (item.total_amount || 0));
  acc.incentive_base_amount = roundAmount(acc.incentive_base_amount + (item.incentive_base_amount || 0));
  acc.incentive_amount      = roundAmount(acc.incentive_amount + (item.incentive_amount || 0));
  return acc;
}, { total_amount: 0, incentive_base_amount: 0, incentive_amount: 0 }));

const recalculate = (item) => {
  item.incentive_amount = parseFloat(((item.incentive_base_amount * item.incentive_percentage) / 100).toFixed(2));
};

const applyGlobal = () => {
  if (globalPercentage.value === null || globalPercentage.value === '') return;
  props.items.forEach(item => {
    item.incentive_percentage = globalPercentage.value;
    recalculate(item);
  });
};

const onSave = (isApprove) => {
  saving.value = true;
  const query = `?company_id=${route.params.companyId}`;

  const payload = {
    company_id:         route.params.companyId,
    contact_profile_id: props.referrerId,
    sale_master_id:     props.saleMaster.id,
    date:               localDate.value,
    is_approve:         isApprove ? 1 : 0,
    total_invoice:      props.saleMaster.subtotal_amount,
    total_discount:     props.saleMaster.total_discount,
    net_bill:           props.saleMaster.total_amount,
    referrer_discount:  props.saleMaster.referrer_discount_amount,
    status: "approved",
    type: 'incentive_payment',
    items: props.items.map(item => ({
      sale_general_id:              item.id,
      product_id:                   item.product_id,
      rate:                         item.rate,
      qty:                          item.quantity,
      total_amount:                 item.total_amount,
      default_incentive_percentage: item.default_incentive_percentage,
      incentive_percentage:         item.incentive_percentage,
      incentive_amount:             item.incentive_amount,
    })),
  };

  storeIncentiveAdjustment(payload, query)
    .then(res => {
      if (!res.status) {
        showError(res.message);
        return;
      }
      showSuccess(res.message);
      emit('saved');
      emit('close');
    })
    .finally(() => {
      saving.value = false;
    });
};
</script>

<style scoped>
.modal-style {
  display: block;
  background: #1c1d1c33;
}
</style>
